import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useSettings } from '@wix/tpa-settings/react';
import {
  TFunction,
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React, { useEffect, useState } from 'react';
import { Divider, Spinner } from 'wix-ui-tpa/cssVars';
import { ExperimentsConsts } from '../../../../consts/experiments';
import { PaymentDetails as PaymentDetailsType } from '../../../../types/dynamicPrice';
import { FormStatus } from '../../../../types/form-state';
import {
  PaymentType as FormPaymentType,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { getContent } from '../../../../utils/content/content';
import { DepositOrFullAmountOptionIds } from '../../../../utils/payment/paymentOptions/utils';
import {
  ServiceData,
  SlotService,
  SelectedProduct,
  V3Product,
} from '../../../../utils/state/types';
import { TagName } from '../../../../utils/tagName.const';
import settingsParams from '../../settingsParams';
import Text, { TextType } from '../../Text/Text';
import { PaymentDetails } from '../PaymentDetails/PaymentDetails';
import { PaymentSummaryDataHook } from './dataHooks';
import { classes, st } from './PaymentSummary.st.css';
import { PriceItem } from './PriceItem/PriceItem';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUp';
import ChevronDownSmall from 'wix-ui-icons-common/on-stage/ChevronDownSmall';
import ChevronUpSmall from 'wix-ui-icons-common/on-stage/ChevronUpSmall';
import { SubscriptionSummary } from './SubscriptionSummary/SubscriptionSummary';
import { SubscriptionDiscounts } from './SubscriptionSummary/SubscriptionDiscounts';
import {
  getProductAddonLines,
  getProductAddonsTotal,
} from './getProductAddonLines';

import { getMaxNumberOfParticipants } from '../../../../utils/numberOfParticipant';
import {
  isLocationOption,
  isRuleBasedOption,
} from '../../../../utils/mappers/dynamic-price.mapper';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { getParticipantHeading } from '../../../../utils/multiParticipantFlow';
import { getParticipantName } from '../../../../utils/multiParticipantFlow/participants';
import { getParticipantSummary } from '../../../../utils/multiParticipantFlow/participantSummary';
import { getParticipantPayment } from '../../../../utils/multiParticipantFlow/participantPayment';
import { FormValues } from '@wix/form-viewer';
import {
  AddonLine,
  getParticipantAddonLines,
} from '../../../../utils/multiParticipantFlow/getParticipantAddonLines';

export type PaymentSummaryProps = {
  serviceData: ServiceData;
  numberOfParticipants: number;
  status: FormStatus;
  isCart: boolean | undefined;
  selectedProductAddons?: SelectedProduct[];
  relatedProducts?: V3Product[];
  isMultiParticipantFlowActive: boolean;
};

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  serviceData,
  numberOfParticipants,
  status,
  isCart,
  selectedProductAddons,
  relatedProducts,
  isMultiParticipantFlowActive,
}) => {
  const { dateRegionalSettingsLocale, participantFormValues } =
    useFormComponentContext();

  const slotService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const {
    paymentDetails,
    dynamicPriceInfo,
    selectedPaymentOption,
    nestedSlot,
  } = slotService;

  const maxNumberOfParticipants = getMaxNumberOfParticipants(
    serviceData,
    getServiceSlotIdentifier(nestedSlot),
  );

  const { t } = useTranslation();
  const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);
  const { experiments } = useExperiments();
  const { isMobile } = useEnvironment();
  const settings = useSettings();

  const isRuleBasedPricingOption = isRuleBasedOption(
    dynamicPriceInfo?.serviceOptionsAndVariants?.options,
  );

  const isLocationPricingOption = isLocationOption(
    dynamicPriceInfo?.serviceOptionsAndVariants?.options,
  );

  const showPricePerParticipant =
    maxNumberOfParticipants > 1 &&
    (!dynamicPriceInfo ||
      isRuleBasedPricingOption ||
      isLocationPricingOption) &&
    !isMultiParticipantFlowActive;

  const sectionTitle = getContent({
    settings,
    settingsParam: settingsParams.summaryPaymentSectionTitle,
  });
  const isMobileSectionTitle =
    isMobile && experiments.enabled(ExperimentsConsts.CollectParticipantForm);
  const [ExpandIcon, CollapseIcon] = isMobileSectionTitle
    ? [ChevronDown, ChevronUp]
    : [ChevronDownSmall, ChevronUpSmall];
  const chevronSize = isMobileSectionTitle ? 30 : undefined;

  const isProcessingPaymentDetails =
    status === FormStatus.PROCESSING_PAYMENT_DETAILS;

  const { tax, subtotalPerParticipant, payNow, payLater, totalPrice } =
    serviceData.summaryPaymentDetails;
  const {
    price,
    priceText,
    currency,
    priceBeforeDiscount,
    discountRuleName,
    discountCalculatedAtCheckout,
    minCharge: deposit,
  } = paymentDetails;

  const isDepositOrFullAmountOption =
    selectedPaymentOption?.id === DepositOrFullAmountOptionIds.Deposit ||
    selectedPaymentOption?.id === DepositOrFullAmountOptionIds.FullAmount;

  const isPayNowNotOnlyDeposit =
    isMultiParticipantFlowActive &&
    (!deposit ||
      !!slotService.participants?.some(
        (_, participantIndex) =>
          getParticipantPayment(slotService, participantIndex)
            .selectedPaymentOption?.id ===
          DepositOrFullAmountOptionIds.FullAmount,
      ));

  const isPricingPlanSelected =
    !selectedPaymentOption?.disabled &&
    ![
      ReservedPaymentOptionIds.SingleSession,
      ReservedPaymentOptionIds.BuyAPricingPlan,
      ReservedPaymentOptionIds.Subscription,
    ].includes(selectedPaymentOption?.id as ReservedPaymentOptionIds) &&
    !isDepositOrFullAmountOption;

  const isSubscriptionSelected =
    selectedPaymentOption?.type === FormPaymentType.SUBSCRIPTION;

  const subscriptionDiscountDetails = isSubscriptionSelected
    ? paymentDetails.subscriptionDiscountDetails
    : undefined;

  const serviceHasPrice = price > 0 || !!priceText || priceBeforeDiscount;
  const showParticipantItems =
    isMultiParticipantFlowActive &&
    (slotService.participants?.length ?? 0) > 1 &&
    (price > 0 || !!priceBeforeDiscount) &&
    !priceText;

  useEffect(() => {
    if (!showParticipantItems) {
      setIsParticipantsExpanded(false);
    }
  }, [showParticipantItems]);
  const showSingleSessionSummary =
    (selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession ||
      isDepositOrFullAmountOption) &&
    serviceHasPrice;

  const isRelatedProductsOnServicesEnabled = experiments.enabled(
    ExperimentsConsts.RelatedProductsOnServices,
  );
  const isMonthlyPricingProductAddOnsEnabled = experiments.enabled(
    ExperimentsConsts.AllowMonthlyPricingProductAddOns,
  );
  const isMultiCurrencyEnabled = experiments.enabled(
    ExperimentsConsts.MultiCurrencyInBookingsUoU,
  );
  const productAddonLines = isRelatedProductsOnServicesEnabled
    ? getProductAddonLines(
        selectedProductAddons ?? [],
        relatedProducts ?? [],
        isMultiCurrencyEnabled ? currency : undefined,
      )
    : [];
  const productAddonsTotal = getProductAddonsTotal(productAddonLines);
  const hasProductAddons = productAddonsTotal > 0;
  const shouldShowSingleSessionDetails =
    (showParticipantItems && !isSubscriptionSelected) ||
    showSingleSessionSummary ||
    (hasProductAddons &&
      !(isSubscriptionSelected && isMonthlyPricingProductAddOnsEnabled));

  const shouldShowPaymentSummary =
    shouldShowSingleSessionDetails ||
    isPricingPlanSelected ||
    isSubscriptionSelected;

  let subtotalItems: PaymentDetailsType[] = [];
  if (dynamicPriceInfo) {
    const defaultPrice = dynamicPriceInfo.defaultPrice;
    subtotalItems = (dynamicPriceInfo.paymentDetails || []).map(
      ({ price: choicePrice, ...rest }) => ({
        ...rest,
        price: choicePrice || {
          currency: defaultPrice!.currency,
          value: defaultPrice!.amount,
        },
      }),
    );
  }

  const priceBeforeDiscountText = priceBeforeDiscount
    ? PriceUtils.getFormattedCurrency({
        price: priceBeforeDiscount,
        currency,
        locale: dateRegionalSettingsLocale,
      })
    : undefined;

  const participantItems = showParticipantItems
    ? getParticipantItems({
        slotService,
        participantFormValues,
        locale: dateRegionalSettingsLocale,
        t,
      })
    : [];

  const participantRows = participantItems.length ? (
    <div id={PARTICIPANTS_ID} hidden={!isParticipantsExpanded}>
      {participantItems.map((item) => (
        <PriceItem
          key={item.dataHook}
          type={TextType.Primary}
          labels={item.labels}
          sublabels={item.sublabels}
          price={item.price}
          ariaPrice={item.ariaPrice}
          dataHook={item.dataHook}
          isCart={isCart}
        />
      ))}
    </div>
  ) : null;

  const priceBreakDownItems = getPriceBreakDownItems({
    locale: dateRegionalSettingsLocale,
    numberOfParticipants,
    showPricePerParticipant,
    tax,
    subtotalPerParticipant: showParticipantItems
      ? undefined
      : subtotalPerParticipant,
    currency,
    t,
    subtotalItems,
    addonLines: isMultiParticipantFlowActive
      ? getParticipantAddonLines(slotService)
      : [],
    priceBeforeDiscountText,
  });

  const totalPriceItems = getTotalPriceItems({
    locale: dateRegionalSettingsLocale,
    price,
    totalPrice: totalPrice + productAddonsTotal,
    payNow,
    payLater,
    deposit,
    productAddonsTotal,
    t,
    currency,
    priceBeforeDiscount,
    isPayNowNotOnlyDeposit,
  });

  return shouldShowPaymentSummary ? (
    <div
      className={st(classes.root, { isProcessingPaymentDetails })}
      data-hook={PaymentSummaryDataHook.Payment_Summary}
    >
      <Text
        tagName={TagName.H3}
        type={isMobileSectionTitle ? TextType.Title : TextType.Primary}
        className={classes.title}
        data-hook={PaymentSummaryDataHook.Title}
      >
        {participantItems.length ? (
          <button
            type="button"
            className={classes.titleToggle}
            data-hook={PaymentSummaryDataHook.Participants_Toggle}
            onClick={() => setIsParticipantsExpanded(!isParticipantsExpanded)}
            aria-expanded={isParticipantsExpanded}
            aria-controls={PARTICIPANTS_ID}
          >
            {sectionTitle}
            {isParticipantsExpanded ? (
              <CollapseIcon size={chevronSize} />
            ) : (
              <ExpandIcon size={chevronSize} />
            )}
          </button>
        ) : (
          sectionTitle
        )}
      </Text>
      {shouldShowSingleSessionDetails ? (
        <div className={classes.singleSession}>
          {isProcessingPaymentDetails ? (
            <div className={classes.spinner}>
              <Spinner
                isCentered
                diameter={24}
                data-hook={PaymentSummaryDataHook.Spinner}
              />
            </div>
          ) : null}
          <div className={classes.details}>
            {priceText && !hasProductAddons ? (
              <div>
                <Text
                  type={TextType.Secondary}
                  data-hook={PaymentSummaryDataHook.Custom_Price}
                >
                  {priceText}
                </Text>
              </div>
            ) : (
              <>
                {participantRows}
                {priceBreakDownItems.map((item, index) => (
                  <PriceItem
                    key={index}
                    type={TextType.Secondary}
                    labels={item.labels}
                    price={item.price}
                    ariaPrice={item.ariaPrice}
                    dataHook={item.dataHook}
                    priceBeforeDiscountText={item.priceBeforeDiscountText}
                    isCart={isCart}
                  />
                ))}
                {participantItems.length || priceBreakDownItems.length ? (
                  <Divider className={classes.divider} />
                ) : null}
                {totalPriceItems.map((totalItem, index) => (
                  <PriceItem
                    key={index}
                    type={TextType.Primary}
                    labels={totalItem.labels}
                    price={totalItem.price}
                    ariaPrice={totalItem.ariaPrice}
                    discountRuleName={
                      totalItem.isTotalPrice && !subscriptionDiscountDetails
                        ? discountRuleName
                        : undefined
                    }
                    discountCalculatedAtCheckout={discountCalculatedAtCheckout}
                    priceBeforeDiscountText={
                      totalItem.isTotalPrice
                        ? priceBeforeDiscountText
                        : undefined
                    }
                    dataHook={totalItem.dataHook}
                    isCart={isCart}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      ) : isSubscriptionSelected ? (
        <SubscriptionSummary
          price={price}
          currency={currency}
          locale={dateRegionalSettingsLocale}
          paymentDetails={paymentDetails}
          numberOfParticipants={numberOfParticipants}
          allowInlinePaymentNumber={isMonthlyPricingProductAddOnsEnabled}
          productAddonLines={
            isMonthlyPricingProductAddOnsEnabled ? productAddonLines : []
          }
          isCart={isCart}
          discountDetails={subscriptionDiscountDetails}
        >
          {participantRows}
        </SubscriptionSummary>
      ) : (
        <PaymentDetails
          paymentOption={selectedPaymentOption}
          payNowAmount={payNow}
          payLaterAmount={payLater}
          currency={currency}
          discountRuleName={discountRuleName}
          discountCalculatedAtCheckout={discountCalculatedAtCheckout}
          priceBeforeDiscountText={priceBeforeDiscountText}
          isCart={isCart}
        />
      )}
      {shouldShowSingleSessionDetails &&
        subscriptionDiscountDetails &&
        paymentDetails.subscriptionInfo && (
          <SubscriptionDiscounts
            discount={subscriptionDiscountDetails.discount}
            calculatedAtCheckout={
              subscriptionDiscountDetails.discountCalculatedAtCheckout
            }
            totalPayments={paymentDetails.subscriptionInfo.totalPayments}
            isCart={isCart}
          />
        )}
      <Divider className={classes.divider} />
    </div>
  ) : null;
};

const PARTICIPANTS_ID = 'payment-summary-participants';

const formatQuantityLine = ({
  t,
  locale,
  quantity,
  name,
  price,
  currency,
}: {
  t: TFunction;
  locale: string;
  quantity: number;
  name: string;
  price: number;
  currency: string;
}) =>
  t('app.payment.summary.number-of-custom-choice.text', {
    numberOfParticipants: quantity,
    choice: name,
    price: PriceUtils.getFormattedCurrency({ price, currency, locale }),
  });

const getParticipantItems = ({
  slotService,
  participantFormValues,
  locale,
  t,
}: {
  slotService: SlotService;
  participantFormValues?: FormValues[];
  locale: string;
  t: TFunction;
}) =>
  (slotService.participants ?? []).flatMap((_, index) => {
    const summary = getParticipantSummary(slotService, index);
    if (!summary) {
      return [];
    }
    const {
      variantName,
      planName,
      isCoveredByPlan,
      addonLines,
      sessionPrice,
      total,
      showTotal,
      currency,
    } = summary;
    const isSubscription =
      getParticipantPayment(slotService, index).selectedPaymentOption?.type ===
      FormPaymentType.SUBSCRIPTION;
    const formatTotal = (currencyDisplay?: 'name') => {
      if (!showTotal) {
        return '';
      }
      const formattedTotal = PriceUtils.getFormattedCurrency({
        price: total,
        currency,
        locale,
        currencyDisplay,
      });
      return isSubscription
        ? t('app.payment.summary.subscription.monthly-price', {
            price: formattedTotal,
          })
        : formattedTotal;
    };
    return [
      {
        labels: [
          getParticipantHeading(
            t,
            index + 1,
            getParticipantName(participantFormValues?.[index]),
          ),
        ],
        sublabels: [
          ...(variantName
            ? [
                isCoveredByPlan
                  ? variantName
                  : formatQuantityLine({
                      t,
                      locale,
                      quantity: 1,
                      name: variantName,
                      price: sessionPrice,
                      currency,
                    }),
              ]
            : []),
          ...(planName ? [planName] : []),
          ...addonLines.map((line) =>
            formatQuantityLine({ t, locale, ...line }),
          ),
        ],
        price: formatTotal(),
        ariaPrice: formatTotal('name'),
        dataHook: `${PaymentSummaryDataHook.Participant}-${index}`,
      },
    ];
  });

const getPriceBreakDownItems = ({
  numberOfParticipants,
  showPricePerParticipant,
  tax,
  subtotalPerParticipant,
  currency,
  locale,
  t,
  subtotalItems,
  addonLines,
  priceBeforeDiscountText,
}: {
  numberOfParticipants: number;
  showPricePerParticipant: boolean;
  tax?: number;
  subtotalPerParticipant?: number;
  currency: string;
  locale: string;
  t: TFunction;
  subtotalItems: PaymentDetailsType[];
  addonLines: AddonLine[];
  priceBeforeDiscountText: string | undefined;
}) => {
  const isSubtotalNeeded =
    showPricePerParticipant || tax || subtotalItems.length > 0;
  const shouldShowSubtotal = subtotalPerParticipant && isSubtotalNeeded;

  return [
    ...(shouldShowSubtotal
      ? [
          {
            labels: [
              t('app.payment.summary.subtotal.text'),
              ...(showPricePerParticipant && !subtotalItems.length
                ? [
                    t('app.payment.summary.number-of-participants.text', {
                      numberOfParticipants,
                      price: PriceUtils.getFormattedCurrency({
                        price: subtotalPerParticipant,
                        currency,
                        locale,
                      }),
                    }),
                  ]
                : []),
              ...subtotalItems.map(
                ({
                  price: choicePrice,
                  numberOfParticipants: choiceNumberOfParticipants,
                  choice,
                }) =>
                  formatQuantityLine({
                    t,
                    locale,
                    quantity: choiceNumberOfParticipants,
                    name: choice,
                    price: Number(choicePrice!.value),
                    currency: choicePrice!.currency!,
                  }),
              ),
              ...addonLines.map((line) =>
                formatQuantityLine({ t, locale, ...line }),
              ),
            ],
            price: PriceUtils.getFormattedCurrency({
              price: subtotalPerParticipant,
              currency,
              locale,
              quantity: numberOfParticipants,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: subtotalPerParticipant,
              currency,
              locale,
              quantity: numberOfParticipants,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Subtotal,
            priceBeforeDiscountText,
          },
        ]
      : []),
    ...(tax
      ? [
          {
            labels: [t('app.payment.summary.tax.text')],
            price: PriceUtils.getFormattedCurrency({
              price: tax,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: tax,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Tax,
          },
        ]
      : []),
  ];
};

const getTotalPriceItems = ({
  price,
  totalPrice,
  payNow,
  payLater,
  currency,
  locale,
  t,
  priceBeforeDiscount,
  productAddonsTotal,
  isPayNowNotOnlyDeposit,
}: {
  price: number;
  totalPrice: number;
  payNow: number | undefined;
  payLater: number | undefined;
  deposit: number;
  productAddonsTotal: number;
  currency: string;
  locale: string;
  t: TFunction;
  priceBeforeDiscount?: number;
  isPayNowNotOnlyDeposit: boolean;
}) => {
  const payNowAmount = payLater ? (payNow ?? 0) + productAddonsTotal : 0;
  const shouldShowPayLaterSection = payLater && payLater !== totalPrice;
  const shouldShowTotalPrice =
    price || priceBeforeDiscount || productAddonsTotal;

  return [
    ...(shouldShowTotalPrice
      ? [
          {
            labels: [t('app.payment.summary.total.text')],
            price: PriceUtils.getFormattedCurrency({
              price: totalPrice,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: totalPrice,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Total_Price,
            isTotalPrice: true,
          },
        ]
      : []),
    ...(payNowAmount
      ? [
          {
            labels: [
              t(
                isPayNowNotOnlyDeposit
                  ? 'app.payment.summary.pay-now.label'
                  : 'app.payment.summary.pay-now.text',
              ),
            ],
            price: PriceUtils.getFormattedCurrency({
              price: payNowAmount,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: payNowAmount,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Deposit,
          },
        ]
      : []),
    ...(shouldShowPayLaterSection
      ? [
          {
            labels: [t('app.payment.summary.pay-later.text')],
            price: PriceUtils.getFormattedCurrency({
              price: payLater,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: payLater,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Pay_Later,
          },
        ]
      : []),
  ];
};
